import React, { useMemo } from 'react';
import { Box, Button, Card, CardContent, Typography, CircularProgress, Alert } from '@mui/material';
import { Login as LoginIcon } from '@mui/icons-material';
import { useAuth } from '@/hooks/useAuth';
import { Navigate, useSearchParams } from 'react-router-dom';
import { authService } from '@/services/AuthService';
import { logger } from '@equip/utils/logger';

// One-shot guard so the automatic session-clear redirect cannot loop.
const SESSION_CLEAR_GUARD = 'token_exchange_session_cleared';

const ERROR_MESSAGES: Record<string, string> = {
  token_exchange_failed: 'Authentication token exchange failed. Please try signing in again.',
  no_code: 'No authorization code received. Please try signing in again.',
  invalid_state: 'Invalid authentication state. Please try signing in again.',
  invalid_token: 'Invalid authentication token. Please try signing in again.',
  session_expired: 'Your session has expired. Please sign in again.',
  access_denied:
    'Access was denied. Please contact your administrator if you believe this is an error.',
  default: 'An authentication error occurred. Please try signing in again.',
};

export const LoginPage: React.FC = () => {
  const { login, isAuthenticated, isLoading } = useAuth();
  const [searchParams] = useSearchParams();

  const errorCode = searchParams.get('error');

  // A token_exchange_failed error is almost always caused by a stale Keycloak
  // SSO cookie handing back a code that then fails to exchange. Those cookies
  // are HttpOnly and cannot be cleared from JS, so redirect once through the
  // Keycloak end-session endpoint to clear them. The one-shot guard prevents a
  // redirect loop if logout itself returns the user here again.
  React.useEffect(() => {
    if (errorCode !== 'token_exchange_failed') return;
    if (sessionStorage.getItem(SESSION_CLEAR_GUARD) === 'true') return;
    sessionStorage.setItem(SESSION_CLEAR_GUARD, 'true');
    logger.warn('Token exchange failed — clearing Keycloak session cookies', {
      component: 'LoginPage',
    });
    authService.clearKeycloakSession();
  }, [errorCode]);

  // Get error from URL parameters
  const errorMessage = useMemo(() => {
    if (!errorCode) return null;
    return ERROR_MESSAGES[errorCode] || ERROR_MESSAGES.default;
  }, [errorCode]);

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Show loading state while checking authentication status
  // This prevents the login page from flashing on refresh when already authenticated
  if (isLoading) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const handleLogin = async () => {
    try {
      await login();
    } catch (error) {
      logger.error('Login failed', { component: 'LoginPage', error: String(error) });
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 400, mx: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              eQuip
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Asset Management System
            </Typography>
          </Box>

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errorMessage}
            </Alert>
          )}

          <Button
            variant="contained"
            fullWidth
            size="large"
            startIcon={<LoginIcon />}
            onClick={handleLogin}
            disabled={isLoading}
            sx={{ mb: 2 }}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>

          <Typography variant="body2" color="text.secondary" align="center">
            Use your organization credentials to access the system
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};
